import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase, SUPABASE_URL } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export interface AdvisorAccess {
  id: string;
  workspace_owner_id: string;
  workspace_id: string | null;
  advisor_email: string;
  advisor_user_id: string | null;
  access_level: "read_only" | "analyst" | "accountant" | "auditor";
  expires_at: string;
  invited_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export function useAdvisorAccess() {
  const queryClient = useQueryClient();
  const { user, getAccessToken } = useAuth();

  // Fetch advisors I've invited (as workspace owner)
  const useMyAdvisorsQuery = () =>
    useQuery({
      queryKey: ["advisor-access", "my-advisors", user?.id],
      queryFn: async (): Promise<AdvisorAccess[]> => {
        const { data, error } = await supabase
          .from("advisor_access")
          .select("*")
          .eq("workspace_owner_id", user!.id)
          .is("revoked_at", null)
          .order("invited_at", { ascending: false });

        if (error) throw error;
        return (data || []) as AdvisorAccess[];
      },
      enabled: !!user,
    });

  // Fetch workspaces where I'm an advisor
  const useMyAdvisorAccessQuery = () =>
    useQuery({
      queryKey: ["advisor-access", "my-access", user?.id],
      queryFn: async (): Promise<AdvisorAccess[]> => {
        const { data, error } = await supabase
          .from("advisor_access")
          .select("*")
          .eq("advisor_user_id", user!.id)
          .is("revoked_at", null)
          .order("accepted_at", { ascending: false });

        if (error) throw error;
        return (data || []) as AdvisorAccess[];
      },
      enabled: !!user,
    });

  const useInviteAdvisorMutation = () =>
    useMutation({
      mutationFn: async ({
        advisorEmail,
        accessLevel = "read_only",
        expiresInDays = 30,
      }: {
        advisorEmail: string;
        accessLevel?: "read_only" | "analyst" | "accountant" | "auditor";
        expiresInDays?: number;
      }) => {
        const token = await getAccessToken();
        if (!token) throw new Error("Authentication required");

        const response = await fetch(`${SUPABASE_URL}/functions/v1/advisor-access`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: "invite",
            advisorEmail,
            accessLevel,
            expiresInDays,
          }),
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || "Failed to invite advisor");
        }

        return response.json();
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["advisor-access"] });
      },
    });

  const useRevokeAdvisorMutation = () =>
    useMutation({
      mutationFn: async (advisorAccessId: string) => {
        const token = await getAccessToken();
        if (!token) throw new Error("Authentication required");

        const response = await fetch(`${SUPABASE_URL}/functions/v1/advisor-access`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: "revoke",
            advisorAccessId,
          }),
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || "Failed to revoke access");
        }

        return response.json();
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["advisor-access"] });
      },
    });

  const useAcceptInvitationMutation = () =>
    useMutation({
      mutationFn: async (invitationToken: string) => {
        const token = await getAccessToken();
        if (!token) throw new Error("Authentication required");

        const response = await fetch(`${SUPABASE_URL}/functions/v1/advisor-access`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: "accept",
            invitationToken,
          }),
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || "Failed to accept invitation");
        }

        return response.json();
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["advisor-access"] });
      },
    });

  return {
    useMyAdvisorsQuery,
    useMyAdvisorAccessQuery,
    useInviteAdvisorMutation,
    useRevokeAdvisorMutation,
    useAcceptInvitationMutation,
  };
}
